import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const cardImageTextVariants = cva(
	'relative overflow-hidden rounded-[24px] transition-all duration-300 ease-in-out',
	{
		variants: {
			color: {
				blue: 'bg-blue-50',
				green: 'bg-green-50',
			},
			state: {
				default: 'border-[2.5px] border-transparent',
				hover: 'border-[2.5px] border-solid',
			},
			size: {
				default: 'w-[220px] max-w-[220px] min-w-[150px]',
				responsive: 'w-full max-w-[220px] min-w-[150px]',
			},
		},
		compoundVariants: [
			{
				color: 'blue',
				state: 'default',
				class: 'border-transparent',
			},
			{
				color: 'blue',
				state: 'hover',
				class: 'border-blue-700',
			},
			{
				color: 'green',
				state: 'default',
				class: 'border-transparent',
			},
			{
				color: 'green',
				state: 'hover',
				class: 'border-green-800',
			},
		],
		defaultVariants: {
			color: 'blue',
			state: 'default',
			size: 'default',
		},
	},
)

const cardImageTextTitleVariants = cva(
	'font-parkinsans text-center text-[16px] leading-[20px] font-medium not-italic md:text-[18px] md:leading-[30px]',
	{
		variants: {
			color: {
				blue: 'text-blue-800',
				green: 'text-green-800',
			},
		},
		defaultVariants: {
			color: 'blue',
		},
	},
)

export interface CardImageTextProps
	extends React.ComponentPropsWithoutRef<'div'>,
		VariantProps<typeof cardImageTextVariants> {
	/**
	 * If set to `true`, the component will be rendered as a child within the component.
	 * This child component must be a valid React component.
	 */
	asChild?: boolean
	/**
	 * Card title text
	 */
	title?: string
	/**
	 * Icon element to display in the card
	 */
	icon?: React.ReactNode
	/**
	 * Additional custom content
	 */
	children?: React.ReactNode
	/**
	 * Height of the component (auto-calculated if not specified)
	 */
	height?: string | number
}

const CardImageText = React.forwardRef<HTMLDivElement, CardImageTextProps>(
	(
		{
			className,
			color = 'blue',
			state = 'default',
			size = 'default',
			asChild = false,
			title = 'Título card',
			icon,
			children,
			height,
			style,
			...props
		},
		ref,
	) => {
		const [isHovered, setIsHovered] = React.useState(false)

		const Comp = asChild ? Slot : 'div'

		// Handle hover states
		const handleMouseEnter = () => setIsHovered(true)
		const handleMouseLeave = () => setIsHovered(false)

		// Determine the current state based on hover
		const currentState = isHovered ? 'hover' : state

		// Calculate component height based on anatomy
		const combinedStyle = React.useMemo(
			() => ({
				height: height || 'auto',
				...style,
			}),
			[height, style],
		)

		if (children) {
			return (
				<Comp
					ref={ref}
					className={cn(
						cardImageTextVariants({ color, state: currentState, size }),
						className,
					)}
					style={combinedStyle}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					{...props}
				>
					{children}
				</Comp>
			)
		}

		return (
			<Comp
				ref={ref}
				className={cn(
					cardImageTextVariants({ color, state: currentState, size }),
					className,
				)}
				style={combinedStyle}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				{...props}
			>
				{/* Content container - Following exact layout and spacing specs */}
				<div className="flex h-full flex-col items-center justify-center overflow-hidden">
					{/* Main content with exact 32px padding from specs */}
					<div className="box-border flex w-full flex-col items-start justify-start gap-2 p-8">
						{/* Content layout - Adjusted gap based on layout specs */}
						<div className="flex w-full flex-col items-center justify-center gap-8">
							{/* Frame 29 - Icon container with exact anatomy specs */}
							<div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-[64px] bg-white">
								{/* Icon container with exact padding */}
								<div className="flex h-full w-full items-center justify-center p-6">
									{icon || (
										<div className="h-10 w-10 shrink-0">
											{/* Default car icon with exact anatomy colors */}
											<svg
												viewBox="0 0 40 40"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												className="h-full w-full"
											>
												<path
													d="M5 25V20C5 18.8954 5.89543 18 7 18H33C34.1046 18 35 18.8954 35 20V25M5 25V30C5 31.1046 5.89543 32 7 32H9C10.1046 32 11 31.1046 11 30V28H29V30C29 31.1046 29.8954 32 31 32H33C34.1046 32 35 31.1046 35 30V25M5 25H35M10 25C8.89543 25 8 24.1046 8 23V20C8 18.8954 8.89543 18 10 18H14C15.1046 18 16 18.8954 16 20V23C16 24.1046 15.1046 25 14 25H10ZM30 25C28.8954 25 28 24.1046 28 23V20C28 18.8954 28.8954 18 30 18H32C33.1046 18 34 18.8954 34 20V23C34 24.1046 33.1046 25 32 25H30Z"
													stroke={
														color === 'blue'
															? 'var(--blue-800)'
															: 'var(--green-800)'
													}
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</div>
									)}
								</div>
							</div>

							{/* Title content with exact layout spacing - 8px top spacing */}
							<div className="mt-2 flex w-full flex-col items-center justify-center gap-2">
								<div
									className={cn(
										cardImageTextTitleVariants({ color }),
										'flex min-w-full flex-col justify-center',
									)}
								>
									{/* Text with exact Parkinsans/18px-Medium specification and width constraint */}
									<p className="max-w-[156px] leading-[30px]">{title}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Comp>
		)
	},
)

CardImageText.displayName = 'CardImageText'

export { CardImageText, cardImageTextVariants, cardImageTextTitleVariants }
